import { aggregateOhlc } from "./LodBucketizer";
export const LOD_WORKER_THRESHOLD = 4_000;
let nextLodWorkerRequestId = 1;
export function shouldUseLodWorker(candles, bucketSize) {
    return bucketSize > 1 && candles.length >= LOD_WORKER_THRESHOLD;
}
function createAbortError() {
    if (typeof DOMException !== "undefined") {
        return new DOMException("LOD aggregation aborted", "AbortError");
    }
    const error = new Error("LOD aggregation aborted");
    error.name = "AbortError";
    return error;
}
const LOD_WORKER_CODE = `
function aggregateOhlc(candles, bucketSize) {
	if (bucketSize <= 1 || candles.length === 0) {
		return candles.map(function(c) {
			return Object.assign({}, c, { sourceCount: 1 });
		});
	}
	var result = [];
	var i = 0;
	while (i < candles.length) {
		var first = candles[i];
		var high = first.high;
		var low = first.low;
		var volume = first.volume;
		var end = Math.min(i + bucketSize, candles.length);
		for (var j = i + 1; j < end; j++) {
			var c = candles[j];
			if (c.high > high) high = c.high;
			if (c.low < low) low = c.low;
			volume += c.volume;
		}
		var last = candles[end - 1];
		result.push({
			time: first.time,
			open: first.open,
			high: high,
			low: low,
			close: last.close,
			volume: volume,
			sourceCount: end - i
		});
		i = end;
	}
	return result;
}

self.onmessage = function(event) {
	var requestId = event.data.requestId;
	var candles = event.data.candles;
	var bucketSize = event.data.bucketSize;
	try {
		var buckets = aggregateOhlc(candles, bucketSize);
		self.postMessage({ requestId: requestId, buckets: buckets });
	} catch (error) {
		self.postMessage({ requestId: requestId, error: error instanceof Error ? error.message : String(error) });
	}
};
`;
function createLodBucketizerWorker() {
    if (typeof Blob === "undefined" || typeof URL === "undefined" || typeof Worker === "undefined") {
        // Fallback for SSR/non-browser environments
        return {
            onmessage: null,
            onerror: null,
            postMessage: () => { },
            terminate: () => { },
        };
    }
    const blob = new Blob([LOD_WORKER_CODE], { type: "application/javascript" });
    const workerUrl = URL.createObjectURL(blob);
    const worker = new Worker(workerUrl);
    const originalTerminate = worker.terminate.bind(worker);
    worker.terminate = () => {
        originalTerminate();
        URL.revokeObjectURL(workerUrl);
    };
    return worker;
}
function resolveWorkerError(event) {
    if (event.error instanceof Error) {
        return event.error;
    }
    if (typeof event.message === "string" && event.message.trim().length > 0) {
        return new Error(event.message);
    }
    return new Error("LOD aggregation worker failed");
}
function runWorkerAggregation(candles, bucketSize, options) {
    const worker = (options.workerFactory ?? createLodBucketizerWorker)();
    const requestId = nextLodWorkerRequestId;
    nextLodWorkerRequestId += 1;
    return new Promise((resolve, reject) => {
        let settled = false;
        const cleanup = () => {
            worker.onmessage = null;
            worker.onerror = null;
            worker.terminate();
            options.signal?.removeEventListener("abort", handleAbort);
        };
        const settle = (callback) => {
            if (settled) {
                return;
            }
            settled = true;
            cleanup();
            callback();
        };
        const handleAbort = () => {
            settle(() => reject(createAbortError()));
        };
        if (options.signal?.aborted) {
            handleAbort();
            return;
        }
        worker.onmessage = (event) => {
            if (event.data.requestId !== requestId) {
                return;
            }
            if (event.data.error) {
                settle(() => reject(new Error(event.data.error)));
                return;
            }
            settle(() => resolve(event.data.buckets ?? []));
        };
        worker.onerror = (event) => {
            settle(() => reject(resolveWorkerError(event)));
        };
        options.signal?.addEventListener("abort", handleAbort, { once: true });
        worker.postMessage({ requestId, candles, bucketSize });
    });
}
export async function aggregateOhlcAsync(candles, bucketSize, options = {}) {
    const useWorker = options.useWorker ?? (typeof Worker !== "undefined" && shouldUseLodWorker(candles, bucketSize));
    if (!useWorker) {
        return aggregateOhlc(candles, bucketSize);
    }
    try {
        return await runWorkerAggregation(candles, bucketSize, options);
    }
    catch (error) {
        if (options.signal?.aborted) {
            throw error;
        }
        return aggregateOhlc(candles, bucketSize);
    }
}
